import React, { useEffect } from 'react';
import styled from '@emotion/native';
import { white } from '@/shared/styles/colors';
import { BodyText, H3 } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useShallow } from 'zustand/react/shallow';
import { useNavigation, useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import useStore from '@/stores/useStore';

const ClientIntro = () => {
  const navigation = useNavigation();
  const router = useRouter();

  const { noStylistAvailable, setNoStylistAvailable } = useStore(
    useShallow((state) => ({
      noStylistAvailable: state.noStylistAvailable,
      setNoStylistAvailable: state.setNoStylistAvailable,
    })),
  );
  const setIsInstantStylingModalVisible = useInstantStylingStore(
    (state) => state.setIsInstantStylingModalVisible,
  );

  useEffect(() => {
    // Reset the client intro screen state when the user navigates away
    const unsubscribe = navigation.addListener('blur', () => {
      setNoStylistAvailable(false);
    });
    return unsubscribe;
  }, [navigation, setNoStylistAvailable]);

  const goToExploreStylists = () => {
    router.push(ROUTES.EXPLORE.path);
  };

  const onPressMainButton = () => {
    if (noStylistAvailable) {
      goToExploreStylists();
    } else {
      setIsInstantStylingModalVisible(true);
    }
  };

  const handleSupportLinkPress = () => {
    router.push(ROUTES.HELP.path);
  };

  return (
    <ContentContainer>
      <TopContentContainer>
        <IntroImageContainer>
          <IntroImage
            source={require('@/assets/images/InstantStylingClientIntro.png')}
          />
        </IntroImageContainer>
        <Title>
          {noStylistAvailable ? `No Stylists\nAvailable` : 'Connect Now'}
        </Title>
        <MainText style={{ marginBottom: 12 }}>
          {noStylistAvailable
            ? `It looks like no stylists are currently online for instant sessions. Come back in a few minutes and try again.`
            : `Get instantly matched with a stylist\nfor a video consultation.`}
        </MainText>
        <MainText>
          {noStylistAvailable
            ? `Want to get styled soon?`
            : `Prefer to choose your stylist?`}
        </MainText>
        <FindStylistText onPress={goToExploreStylists}>
          Find a stylist and schedule a call
        </FindStylistText>
        <Button
          variant="secondary"
          text={
            noStylistAvailable ? 'Explore Stylists' : 'Curate Your Experience'
          }
          onPress={onPressMainButton}
          size="medium"
          customStyle={curateExperienceButtonStyle}
        />
      </TopContentContainer>
      <BottomContentContainer>
        <MainText style={{ fontSize: 12 }}>
          Learn more about{' '}
          <BoldLinkText onPress={handleSupportLinkPress}>
            Instant Calls
          </BoldLinkText>
        </MainText>
      </BottomContentContainer>
    </ContentContainer>
  );
};

export default ClientIntro;

const ContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 56,
  paddingHorizontal: 30,
  paddingBottom: 64,
});

const TopContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Title = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  color: white,
  marginBottom: 16,
});

const IntroImageContainer = styled.View({
  width: 235,
  height: 163,
  borderRadius: 82,
  marginBottom: 36,
});

const IntroImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const MainText = styled(BodyText)({
  fontSize: 16,
  lineHeight: 18,
  textAlign: 'center',
  color: white,
});

const FindStylistText = styled(BodyText)({
  fontSize: 16,
  lineHeight: 18,
  fontWeight: 'bold',
  textAlign: 'center',
  color: white,
  textDecorationLine: 'underline',
  marginBottom: 24,
});

const curateExperienceButtonStyle = {
  paddingHorizontal: 24,
  marginBottom: 64,
};

const BottomContentContainer = styled.View({});

const BoldLinkText = styled(BodyText)({
  fontSize: 12,
  fontWeight: 'bold',
  color: white,
  textDecorationLine: 'underline',
});
